/**
 * CloudEmergencyTable — real-time emergency alerts table for Cloud 5.0 platform.
 * Source: activeCloudAlarms from NotificationContext.
 *
 * Columns: Event Start, Duration, Category/Type, Device/Serial, Description,
 *          Area, Resident, Accepted By/Time, Device Cleared, Status
 *
 * Schema uses snake_case fields (event_id, event_datetime_utc, event_category, etc.)
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow, durationBetween } from '../../../../libs.jsx';
import AlertStatusBadge from './AlertStatusBadge.jsx';
import AlertTypeBadge from './AlertTypeBadge.jsx';
import BlockedReasonTip from '../../../../components/v2/BlockedReasonTip.jsx';

/** Hook that ticks every second to force duration re-renders */
function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const OVERDUE_MS = 5 * 60 * 1000;
const CRITICAL_MS = 10 * 60 * 1000;

const getDurationColor = (status, eventStart) => {
    if (status !== 'Overdue' || !eventStart) return undefined;
    const elapsed = Date.now() - new Date(eventStart.endsWith?.('Z') ? eventStart : eventStart + 'Z').getTime();
    if (elapsed > CRITICAL_MS) return 'var(--color-danger, #e63757)';
    if (elapsed > OVERDUE_MS) return 'var(--color-warning-text, #d97706)';
    return undefined;
};

/** Duration cell — green when cleared, yellow/red when overdue, popover for response time */
const DurationCell = ({ eventStart, ackTime, status }) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const ref = useRef(null);
    const hasAck = Boolean(ackTime);

    const handleEnter = () => {
        if (!hasAck || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const popoverWidth = 260;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceRight = window.innerWidth - rect.left;
        const horizontal = spaceRight < popoverWidth + 16
            ? { right: `${window.innerWidth - rect.right}px`, left: 'auto' }
            : { left: `${rect.left}px`, right: 'auto' };
        const vertical = spaceBelow < 80
            ? { bottom: `${window.innerHeight - rect.top + 6}px`, top: 'auto' }
            : { top: `${rect.bottom + 6}px`, bottom: 'auto' };
        setStyle({ position: 'fixed', ...horizontal, ...vertical });
        setOpen(true);
    };

    const color = hasAck
        ? 'var(--color-success-text, #0cce6b)'
        : status === 'Accepted'
            ? 'var(--color-primary, #0081C7)'
            : getDurationColor(status, eventStart);

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)}>
            <span className={`na-duration${hasAck ? ' na-duration--has-popover' : ''}`} style={color ? { color } : undefined}>
                {durationToNow(eventStart)}
            </span>
            {open && hasAck ? (
                <div className="na-status-popover" style={style}>
                    <strong>Response Time:</strong> {durationBetween(eventStart, ackTime)}
                </div>
            ) : null}
        </span>
    );
};

const CloudEmergencyTable = ({
    data, sortColumn, sortOrder, onSort,
    canInteract = false, pendingEventId = null, onAccept, onRelease, onFinish, onAcknowledge, onViewVideo,
    acceptBlockedReason = () => null,
    ackIntegrationActive = false, canViewVideo = false,
    highPriorityTypeIds = new Set(),
    sessionUserId = null, sessionUsername = null,
}) => {
    useTick(1000); // Re-render every second for live duration updates

    // Actions column renders when portal interactions are on (Accept/Finish) OR an
    // acknowledge integration is active for this campus (e.g. SafelyYou \u2192 the
    // Acknowledge link button below), so it shows even when portal_afr is off.
    const showActions = Boolean(canInteract) || Boolean(ackIntegrationActive);
    const colSpan = showActions ? 12 : 11;

    /** Row-level action buttons.
     *  Accept / Finish are portal actions (gated on canInteract = portal_afr).
     *  Acknowledge is the SafelyYou action (gated on ackIntegrationActive), for
     *  event_type_id 40 alerts that carry an acknowledge_link and are pre-clear:
     *   - Acknowledge (POST) \u2014 must be accepted first (unless accept isn't required)
     *   - View Video (GET link, external) \u2014 role_weight > 10 only, any accept state */
    const renderRowActions = (row) => {
        const accepted = Boolean(row.accepted_display) || Boolean(row.accepted_datetime_utc);
        const hasAck = Boolean(row.ack_datetime_utc); // device cleared
        const acceptReq = Boolean(row.accept_required);
        const isType40 = (row.event_type_id ?? '').toString() === '40';

        /* Only the caregiver who accepted may finish or acknowledge. Same identity
           test the mobile card uses (MobileAlertCard.jsx): prefer accepted_user_id,
           fall back to accepted_username so a row written before that id existed
           still resolves. A missing value on either side means "not me", so this
           fails closed rather than open. */
        const acceptedByMe = (
            (row.accepted_user_id != null && sessionUserId != null
                && String(row.accepted_user_id) === String(sessionUserId))
            || (row.accepted_username && sessionUsername
                && String(row.accepted_username) === String(sessionUsername))
        );

        // Portal accept/finish \u2014 require portal_afr.
        // A cleared-but-unaccepted alert can still be accepted.
        const showAccept = canInteract && acceptReq && !accepted
            && (row.status === 'Waiting' || row.status === 'Overdue' || hasAck);
        // An alert is finished by whoever accepted it and nobody else. When
        // acceptance was never required there is no accepter to defer to, so the
        // restriction doesn't apply.
        const showFinish = canInteract && Boolean(row.finish_required) && row.status === 'Arrived'
            && (!acceptReq || acceptedByMe);

        /* Release — hand an accepted alert back so someone else can take it.
           Accepter only, by decision: a caregiver can give up an alert they cannot
           attend, but nobody can take one out from under an active response.

           Status 'Accepted' already means accepted-and-not-yet-cleared (see
           deriveCloudAlarmStatus), so hasAck is redundant with it today. It is
           tested anyway because that derivation lives in another module and this
           button must never appear on a cleared alert. */
        const showRelease = canInteract && row.status === 'Accepted' && !hasAck && acceptedByMe;

        /* Non-null when this user is already holding a different alert. Accept stays
           rendered — disabled, with the reason on hover — rather than vanishing: a
           button that silently disappears reads as a bug, and the reason ("finish or
           release the one you have") is the whole point of the rule. */
        const acceptBlocked = showAccept ? acceptBlockedReason(row) : null;

        // SafelyYou acknowledge capability for this row (pre-clear, has link).
        const ackEligible = ackIntegrationActive && isType40 && Boolean(row.acknowledge_link) && !hasAck;
        // Acknowledge (POST) is the accepter's call: when the alert required
        // acceptance it must have been accepted BY THIS USER. This previously
        // checked only that *someone* had accepted, which let a second caregiver
        // acknowledge another's alert. An alert that never required acceptance has
        // no accepter, so anyone may acknowledge it. View Video (the GET link)
        // stays open to role_weight > 10 regardless of accept state.
        const canAcknowledge = acceptReq ? acceptedByMe : true;
        const showAcknowledge = ackEligible && canAcknowledge;
        const showViewVideo = ackEligible && canViewVideo;

        const pending = pendingEventId === row.event_id;

        if (!showAccept && !showRelease && !showFinish && !showAcknowledge && !showViewVideo) {
            return <span className="na-muted">{'\u2014'}</span>;
        }

        return (
            <div className="na-row-actions">
                {showAccept ? (
                    <BlockedReasonTip message={acceptBlocked || ''}>
                        <button
                            type="button"
                            className="na-action-btn na-action-btn--accept"
                            onClick={() => onAccept && onAccept(row)}
                            disabled={pending || !onAccept || Boolean(acceptBlocked)}
                            title={acceptBlocked ? undefined : 'Accept this alert'}
                        >
                            {pending ? 'Accepting\u2026' : 'Accept'}
                        </button>
                    </BlockedReasonTip>
                ) : null}
                {showRelease ? (
                    <button
                        type="button"
                        className="na-action-btn na-action-btn--release"
                        onClick={() => onRelease && onRelease(row)}
                        disabled={pending || !onRelease}
                        title="Release this alert so another caregiver can accept it"
                    >
                        {pending ? 'Releasing\u2026' : 'Release'}
                    </button>
                ) : null}
                {showFinish ? (
                    <button
                        type="button"
                        className="na-action-btn na-action-btn--finish"
                        onClick={() => onFinish && onFinish(row)}
                        disabled={pending || !onFinish}
                        title="Finish this alert"
                    >
                        {pending ? 'Finishing\u2026' : 'Finish'}
                    </button>
                ) : null}
                {showAcknowledge ? (
                    <button
                        type="button"
                        className="na-action-btn na-action-btn--acknowledge"
                        onClick={() => onAcknowledge && onAcknowledge(row)}
                        disabled={pending || !onAcknowledge}
                        title="Acknowledge this fall alert"
                    >
                        {pending ? 'Acknowledging…' : 'Acknowledge'}
                    </button>
                ) : null}
                {showViewVideo ? (
                    <a
                        className="na-action-btn na-action-btn--view-video"
                        href={row.acknowledge_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open SafelyYou to view the video (opens in a new tab)"
                        onClick={() => onViewVideo && onViewVideo(row)}
                    >
                        View Video
                        <i className="fe fe-external-link" aria-hidden="true" />
                    </a>
                ) : null}
            </div>
        );
    };

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>;
    };

    const formatUtcToLocal = (utcStr) => {
        if (!utcStr) return '\u2014';
        try {
            const d = new Date(utcStr.endsWith('Z') ? utcStr : utcStr + 'Z');
            return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
        } catch { return utcStr; }
    };

    const getDeviceLink = (row) => {
        const mfg = parseInt(row.manufacture_id, 10);
        if (mfg === 1) return `/profiles/inovonics/${row.device_id}`;
        if (mfg === 4) return `/profiles/palcare/${row.device_id}`;
        return null;
    };

    const getBorderColor = (category) => {
        const cat = (category || '').toLowerCase();
        if (cat === 'emergency') return '#e63757';
        if (cat === 'maintenance') return '#ffc107';
        return '#0081C7';
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('event_datetime_utc')}>Event Start{sortIcon('event_datetime_utc')}</th>
                        <th className="dp-th">Duration</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('event_category')}>Category / Type{sortIcon('event_category')}</th>
                        <th className="dp-th">Device / Serial</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('device_description')}>Description{sortIcon('device_description')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('area_name')}>Area{sortIcon('area_name')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('near_area_name')}>Near Area{sortIcon('near_area_name')}</th>
                        <th className="dp-th">Resident</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('accepted_display')}>Accepted By / Time{sortIcon('accepted_display')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('ack_datetime_utc')}>Device Cleared{sortIcon('ack_datetime_utc')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('status')}>Status{sortIcon('status')}</th>
                        {showActions ? <th className="dp-th">Actions</th> : null}
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={colSpan}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">{'\u2713'}</div>
                                    <div className="na-empty__text">No active emergency alerts</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row) => {
                        const deviceLink = getDeviceLink(row);
                        const isHighPriority = highPriorityTypeIds.has((row.event_type_id ?? '').toString());
                        return (
                            <tr
                                key={row.event_id}
                                className={`dp-tr na-row-border${isHighPriority ? ' na-row--priority' : ''}`}
                                style={{ borderLeftColor: isHighPriority ? 'var(--color-danger, #e63757)' : getBorderColor(row.event_category) }}
                            >
                                <td className="dp-td" style={{ whiteSpace: 'break-spaces' }}>{formatUtcToLocal(row.event_datetime_utc)}</td>
                                <td className="dp-td">
                                    <DurationCell eventStart={row.event_datetime_utc} ackTime={row.ack_datetime_utc} status={row.status} />
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {isHighPriority ? (
                                            <span className="na-badge na-badge--priority" title="High priority event type">
                                                {'⚑'} High Priority
                                            </span>
                                        ) : null}
                                        <AlertTypeBadge alertType={row.event_category} alertSubType={row.event_type} />
                                    </div>
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {deviceLink ? (
                                            <Link to={deviceLink} className="ar-link">{row.device_name || '\u2014'}</Link>
                                        ) : (<span>{row.device_name || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.manufacture_uuid || ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td" style={{ whiteSpace: 'break-spaces' }}>{row.device_description || '\u2014'}</td>
                                <td className="dp-td">
                                    {row.area_id ? (
                                        <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{row.area_name || '\u2014'}</Link>
                                    ) : (row.area_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    {row.near_area_id ? (
                                        <Link to={`/profiles/area/${row.near_area_id}`} className="ar-link">{row.near_area_name || '\u2014'}</Link>
                                    ) : (row.near_area_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    {row.residents?.length ? (
                                        <div className="na-stack">
                                            {row.residents.map((r, i) => (
                                                r.resident_id ? (
                                                    <Link key={r.resident_id} to={`/profiles/resident/${r.resident_id}`} className="ar-link">{r.resident_name || '\u2014'}</Link>
                                                ) : (<span key={`r-${i}`}>{r.resident_name || '\u2014'}</span>)
                                            ))}
                                        </div>
                                    ) : '\u2014'}
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {row.accepted_user_id ? (
                                            <Link to={`/profiles/user/${row.accepted_user_id}`} className="ar-link">{row.accepted_display || '\u2014'}</Link>
                                        ) : (<span>{row.accepted_display || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.accepted_datetime_utc ? formatUtcToLocal(row.accepted_datetime_utc) : ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td" style={{ whiteSpace: 'break-spaces' }}>{formatUtcToLocal(row.ack_datetime_utc)}</td>
                                <td className="dp-td"><AlertStatusBadge status={row.status} eventDateTime={row.event_datetime_utc} /></td>
                                {showActions ? (
                                    <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>
                                        {renderRowActions(row)}
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default CloudEmergencyTable;
